import * as React from "react"
import type { MergedDeviceType } from "@/types"
import Cookies from "js-cookie"

import type { ConsumerConnectionBillingStatusType } from "@/types/billing"
import { PositionProvider } from "@/components/providers/position-provider"


import { TrackingMapLibre } from "../tracking-map-libre"

type Coordinates = [number, number]

interface MapRendererProps {
  picker?: { lat: number; lng: number }
  onPositionChange?: (position: [number, number]) => void
  markers?: ConsumerConnectionBillingStatusType[]
  trackMarkers?: MergedDeviceType[]
  selectedLocation?: [number, number] | null
  kmzFiles?: Array<{ id: string | number; s3_url: string }>
}

export function MapRenderer({
  picker,
  onPositionChange,
  markers,
  trackMarkers,
  selectedLocation,
  kmzFiles,
}: MapRendererProps) {
  const [currentLocation, setCurrentLocation] =
    React.useState<Coordinates | null>(null)
  const [locationObtained, setLocationObtained] = React.useState(false)

  React.useEffect(() => {
    const fetchLocation = () => {
      const userClientInfo = Cookies.get("user_client_info")
      if (userClientInfo) {
        const { lat, lon } = JSON.parse(userClientInfo)
        setCurrentLocation([lat, lon])
      } else {
        setCurrentLocation([14.5995, 120.9842])
      }
      setLocationObtained(true)
    }
    fetchLocation()
  }, [])

  // Middle‑click reload
  React.useEffect(() => {
    let lastClickTime = 0
    const doubleClickThreshold = 300
    const handleMouseDown = (event: MouseEvent) => {
      if (event.button === 1) {
        const currentTime = new Date().getTime()
        if (currentTime - lastClickTime < doubleClickThreshold) {
          window.location.reload()
        }
        lastClickTime = currentTime
      }
    }
    window.addEventListener("mousedown", handleMouseDown)
    return () => window.removeEventListener("mousedown", handleMouseDown)
  }, [])

  if (!locationObtained) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>Loading map...</p>
      </div>
    )
  }

  const mapPosition = selectedLocation || currentLocation! 

  // Picker mode → lazy load Leaflet map (named export)
   if (picker) {
    const MapPicker = React.lazy(() =>
      import("../picker/map-picker").then((module) => ({
        default: module.MapPicker, // named export
      })),
    )
    return (
      <React.Suspense fallback={<div>Loading picker...</div>}>
        {onPositionChange ? (
          <PositionProvider onPositionChange={onPositionChange}>
            <MapPicker position={[picker.lat, picker.lng]} />
          </PositionProvider>
        ) : (
          <MapPicker position={[picker.lat, picker.lng]} />
        )}
      </React.Suspense>
    )
  }

  // Tracking mode → MapLibre
  return (
    <div className="h-screen w-full">
      <TrackingMapLibre
        position={mapPosition}
        markers={markers}
        trackMarkers={trackMarkers}
        selectedLocation={selectedLocation}
        kmzFiles={kmzFiles}
      />
    </div>
  )
}
